import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { SalesService } from '../services/salesService';
import { computeSale, SALE_FILTERS, PAYMENT_FILTERS, filterSales, todayRevenue, todayCount, outstandingBalance, outstandingCount } from '../utils/salesUtils';

export function useSales() {
  const { authorizedFetch } = useAuth();

  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState(SALE_FILTERS.ALL);
  const [paymentFilter, setPaymentFilter] = useState(PAYMENT_FILTERS.ALL);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [counts, setCounts] = useState({
    all: 0, paid: 0, partial: 0, due: 0,
    Cash: 0, Card: 0, Transfer: 0
  });

  const [summary, setSummary] = useState({
    todayRevenue: 0,
    todayCount: 0,
    outstanding: 0,
    outstandingCount: 0,
  });

  const loadSummary = useCallback(async () => {
    try {
      const data = await SalesService.getSummary(authorizedFetch);
      if (data) {
        setSummary({
          todayRevenue: data.todayRevenue || 0,
          todayCount: data.todayCount || 0,
          outstanding: data.outstandingBalance || 0,
          outstandingCount: data.outstandingCount || 0,
        });
      }
    } catch (err) {
      console.error('Error loading summary:', err);
    }
  }, [authorizedFetch]);

  const loadSales = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const data = await SalesService.getSales(authorizedFetch, { page: 1, limit: 20, ...params });
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        setSales(data.data || []);
        setPage(data.page || 1);
        setTotalPages(data.totalPages || 1);
        if (data.counts) {
          setCounts(data.counts);
        }
      } else {
        setSales(Array.isArray(data) ? data : []);
        setPage(1);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error loading sales:', err);
      // Backend may not exist yet — fail gracefully so UI renders
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch]);

  const getSaleById = useCallback(async (id) => {
    try {
      const sale = await SalesService.getSale(id, authorizedFetch);
      return sale;
    } catch (err) {
      console.error('Error fetching sale by ID:', err);
      return null;
    }
  }, [authorizedFetch]);

  useEffect(() => {
    loadSales();
    loadSummary();
  }, [loadSales, loadSummary]);

  const createSale = useCallback(async (dogIds, items, discount, opts = {}) => {
    try {
      const dogsArr = Array.isArray(dogIds) ? dogIds.filter(Boolean) : (dogIds ? [dogIds] : []);
      const payload = {
        items,
        discount,
        earnCashback: opts.earnCashback !== false,
        internalNote: opts.internalNote || '',
      };
      if (dogsArr.length > 0) {
        payload.dog  = dogsArr[0];
        payload.dogs = dogsArr;
      }
      const sale = await SalesService.createSale(payload, authorizedFetch);
      setSales(prev => [sale, ...prev]);
      loadSummary();
      return sale;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [authorizedFetch, loadSummary]);

  const updateSale = useCallback(async (id, patch) => {
    try {
      const updated = await SalesService.updateSale(id, patch, authorizedFetch);
      setSales(prev => prev.map(s => s._id === id || s.id === id ? updated : s));
      loadSummary();
      return updated;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [authorizedFetch, loadSummary]);

  const addPayment = useCallback(async (saleId, payment) => {
    try {
      const updated = await SalesService.addPayment(saleId, payment, authorizedFetch);
      setSales(prev => prev.map(s => s._id === saleId || s.id === saleId ? updated : s));
      loadSummary();
      return updated;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [authorizedFetch, loadSummary]);

  const deletePayment = useCallback(async (saleId, paymentId, optimisticFn) => {
    // Optimistic remove
    setSales(prev => prev.map(s => {
      if (s._id !== saleId && s.id !== saleId) return s;
      return optimisticFn ? optimisticFn(s) : s;
    }));
    try {
      const updated = await SalesService.deletePayment(saleId, paymentId, authorizedFetch);
      setSales(prev => prev.map(s => s._id === saleId || s.id === saleId ? updated : s));
      loadSummary();
      return updated;
    } catch (err) {
      setError(err.message);
      await loadSales(); // rollback
      await loadSummary();
      return null;
    }
  }, [authorizedFetch, loadSales, loadSummary]);

  const cancelSale = useCallback(async (saleId) => {
    try {
      const updated = await SalesService.cancelSale(saleId, authorizedFetch);
      setSales(prev => prev.map(s => s._id === saleId || s.id === saleId ? updated : s));
      loadSummary();
      return updated;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [authorizedFetch, loadSummary]);

  const clearError = useCallback(() => setError(null), []);

  return {
    sales, allSales: sales, loading, error,
    page, setPage, totalPages, counts,
    filterType, setFilterType,
    paymentFilter, setPaymentFilter,
    summary,
    createSale, updateSale, cancelSale, addPayment, deletePayment,
    loadSales, getSaleById, clearError,
    computeSale,
    SALE_FILTERS,
  };
}
